/**
 * CompanyFormDialog — create/edit a company (yritysrekisteri, #763/#764)
 *
 * Field rendering, validation and payload building are shared with the inline
 * company creation in the work-order create flow via CompanyFormFields (#1002).
 *
 * A shared Y-tunnus warns instead of failing (#2410). Choosing an existing
 * company calls `onCreated` with that company, which is how a nested caller
 * (party picker, work-order party dialog) selects it without inserting a row.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { DialogContent, Stack } from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import {
  CompanyFormFields,
  initialCompanyFieldsData,
  companyFieldsToPayload,
  readCompanyConsolidationFlags,
  validateCompanyFields,
  type CompanyFieldsData,
} from './CompanyFormFields';
import { BusinessIdConflictDialog } from './BusinessIdConflictDialog';
import {
  lookupCompaniesByBusinessId,
  useCreateCompany,
  useUpdateCompany,
  type BusinessIdMatch,
  type Company,
  type CreateCompanyData,
} from '@/hooks/useCompanies';
import { useNotify } from '@/stores/notificationStore';
import { ApiError } from '@/lib/api';

interface CompanyFormDialogProps {
  open: boolean;
  company?: Company | null;
  onClose: () => void;
  onCreated?: (company: Company) => void;
}

function needsBusinessIdWarning(
  data: CreateCompanyData,
  editing: Company | null | undefined
): boolean {
  if (!data.businessId) return false;
  if (!editing) return true;
  const nameChanged = editing.name !== data.name;
  const businessIdChanged = (editing.businessId ?? null) !== data.businessId;
  return nameChanged || businessIdChanged;
}

export function CompanyFormDialog({ open, company, onClose, onCreated }: CompanyFormDialogProps) {
  const { t } = useTranslation('companies');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const navigate = useNavigate();
  const isEditing = !!company;

  const [formData, setFormData] = useState<CompanyFieldsData>(initialCompanyFieldsData);
  const [errors, setErrors] = useState<Partial<Record<keyof CompanyFieldsData, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [conflict, setConflict] = useState<BusinessIdMatch[] | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  // Bumped when the dialog closes or the edited company changes, so a lookup
  // that was already in flight cannot reopen the warning on the next session.
  const sessionRef = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;

  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();

  const isLive = (session: number) => openRef.current && sessionRef.current === session;

  useEffect(() => {
    sessionRef.current += 1;
    setConflict(null);
    setIsChecking(false);
    if (company) {
      const consolidation = readCompanyConsolidationFlags(company);
      setFormData({
        name: company.name,
        businessId: company.businessId ?? '',
        phone: company.phone ?? '',
        email: company.email ?? '',
        website: company.website ?? '',
        streetAddress: company.streetAddress ?? '',
        postalCode: company.postalCode ?? '',
        city: company.city ?? '',
        country: company.country,
        billingStreetAddress: company.billingStreetAddress ?? '',
        billingPostalCode: company.billingPostalCode ?? '',
        billingCity: company.billingCity ?? '',
        invoiceEmail: company.invoiceEmail ?? '',
        eInvoiceAddress: company.eInvoiceAddress ?? '',
        ovtId: company.ovtId ?? '',
        eInvoiceOperator: company.eInvoiceOperator ?? '',
        paymentTermsDays: company.paymentTermsDays != null ? String(company.paymentTermsDays) : '',
        notes: company.notes ?? '',
        laborRateId: company.laborRateId ?? '',
        consolidatedInvoicing: consolidation.consolidatedInvoicing ? 'true' : 'false',
        consolidatedAutoSend: consolidation.consolidatedAutoSend ? 'true' : 'false',
      });
    } else {
      setFormData(initialCompanyFieldsData);
    }
    setErrors({});
    setIsDirty(false);
  }, [company, open]);

  const validate = (): boolean => {
    const next = validateCompanyFields(formData, t);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (field: keyof CompanyFieldsData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const lookupInput = (data: CreateCompanyData) => ({
    businessId: data.businessId ?? '',
    name: data.name,
    ...(company ? { excludeId: company.id } : {}),
  });

  const persist = async (data: CreateCompanyData, allowDuplicate: boolean, session: number) => {
    const payload = allowDuplicate ? { ...data, allowDuplicateBusinessId: true } : data;
    try {
      if (company) {
        await updateMutation.mutateAsync({ id: company.id, data: payload });
        if (!isLive(session)) return;
      } else {
        const created = await createMutation.mutateAsync(payload);
        if (!isLive(session)) return;
        onCreated?.(created);
      }
      notify.success(t('saved'), t('savedDesc', { name: data.name }));
      setConflict(null);
      onClose();
    } catch (err) {
      if (!isLive(session)) return;
      if (err instanceof ApiError && err.status === 409 && data.businessId) {
        try {
          const recovered = await lookupCompaniesByBusinessId(lookupInput(data));
          if (!isLive(session)) return;
          if (recovered.matches.length > 0) {
            setConflict(recovered.matches);
            return;
          }
        } catch {
          if (!isLive(session)) return;
        }
        notify.error(
          t('businessIdExists'),
          t('businessIdExistsDesc', { businessId: data.businessId })
        );
        return;
      }
      notify.error(tc('error'), describeApiError(err, tc, { fallback: t('saveFailed') }));
    }
  };

  const handleSubmit = async () => {
    if (!validate() || conflict) return;
    const data = companyFieldsToPayload(formData);
    const session = sessionRef.current;

    if (needsBusinessIdWarning(data, company)) {
      const businessId = data.businessId;
      if (businessId) {
        setIsChecking(true);
        try {
          const lookup = await lookupCompaniesByBusinessId(lookupInput(data));
          if (!isLive(session)) return;
          if (lookup.matches.length > 0) {
            setConflict(lookup.matches);
            return;
          }
        } catch {
          if (!isLive(session)) return;
          // Save still rejects an unconfirmed duplicate with 409, and that 409
          // looks the warning up again.
        } finally {
          if (sessionRef.current === session) setIsChecking(false);
        }
      }
    }

    if (!isLive(session)) return;
    await persist(data, false, session);
  };

  const handleUseExisting = (existing: Company) => {
    if (onCreated) {
      onCreated(existing);
    } else {
      navigate(`/parties/companies/${existing.id}`);
    }
    notify.success(
      t('businessIdConflict.usingExisting'),
      t('businessIdConflict.usingExistingDesc', { name: existing.name })
    );
    setConflict(null);
    onClose();
  };

  const handleConfirmBranch = () => {
    const data = companyFieldsToPayload(formData);
    void persist(data, true, sessionRef.current);
  };

  const isPending = createMutation.isPending || updateMutation.isPending || isChecking;

  return (
    <>
      <FormDialog
        open={open}
        onClose={onClose}
        title={isEditing ? t('editCompany') : t('addCompany')}
        isDirty={isDirty}
        onSubmit={handleSubmit}
        isLoading={isPending}
        submitLabel={isEditing ? tc('save') : tc('add')}
        maxWidth="md"
      >
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <CompanyFormFields data={formData} errors={errors} onChange={handleChange} />
          </Stack>
        </DialogContent>
      </FormDialog>
      <BusinessIdConflictDialog
        open={conflict != null}
        mode={isEditing ? 'edit' : 'create'}
        businessId={formData.businessId.trim()}
        matches={conflict ?? []}
        onUseExisting={handleUseExisting}
        onConfirmBranch={handleConfirmBranch}
        onCancel={() => setConflict(null)}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />
    </>
  );
}
